import {
  ActingUserDto,
  FilterOptionDto,
} from '@act/shared/data-transfer-objects';
import { Typography } from '@mui/material';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import {
  getMemberFilters,
  getMembersForMap,
  getPaginatedMembersForTable,
} from 'redux/slices/memberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import FilterDialog from './filter-dialog';
import MemberFilters from './MemberFilters';
import MembersTable from './MembersTable';
import { UserRole } from '@act/shared/definitions';
import { useMobile } from '@act/utilities/mobile-context';

import { AppHeaderPortal, FlexContainer } from '@act/storybook';
import {
  BaseFilterConfig,
  MapFilterConfig,
  PaginatedFilterConfig,
} from './types';

// Lazy load the map component since it's only used in map view
const MemberMap = lazy(() => import('../maps/MemberMap'));

export const MemberIndex = () => {
  const { isMobile } = useMobile();
  const [viewMode, setViewMode] = useState<'list-view' | 'map-view'>(
    'list-view'
  );
  const [open, setOpen] = useState<boolean>(false);
  const [filterConfig, setFilterConfig] = useState<BaseFilterConfig>(null);

  const actingUser: ActingUserDto = useAppSelector(
    state => state.actingUser.user
  );
  const membersLoading = useAppSelector(state => state.members.status);
  const filters = useAppSelector(state => state.members.filters);

  useEffect(() => {
    async function getFilters() {
      try {
        await dispatch(await getMemberFilters({}));
      } catch (err) {
        console.error(`Error getting filters for members index: ${err}`);
      }
    }

    if (!filters?.length) {
      void getFilters();
    }
  }, []);

  function getDefaults(reset?: boolean): {
    [key: string]: unknown[] | boolean | number | string;
  } {
    const existingConfig = window.localStorage.getItem('filterConfig');
    if (existingConfig && !reset) return JSON.parse(existingConfig);

    const defaults = {
      searchTerm: '',
      guideIds: [],
      showOnlyMyMembers: actingUser?.role === UserRole.ADMIN ? false : true,
      includeCount: true,
      'page-start': 0,
      'page-size': 10,
      orderBy: undefined,
      orderDirection: undefined,
      includeDetails: true,
      includeStageAndStatus: true,
    };
    filters?.forEach(f => (defaults[f.key] = []));
    return defaults;
  }

  useEffect(() => {
    function setDefaults() {
      const stringifiedFilterConfig =
        window.localStorage.getItem('filterConfig');
      const parsedFilterConfig = JSON.parse(stringifiedFilterConfig);

      const defaults = getDefaults();

      const reconciledDefaults = {
        ...defaults,
        ...parsedFilterConfig,
      };
      reset(reconciledDefaults);
      void handleFiltering();
    }
    if (filters.length) {
      setDefaults();
    }
  }, [filters]);

  useEffect(() => {
    if (!filterConfig || membersLoading === 'loading') {
      return;
    } else if (filterConfig['forMap'] === true) {
      void getMembersForMapView(filterConfig as unknown as MapFilterConfig);
    } else {
      void getPaginatedMembers(
        filterConfig as unknown as PaginatedFilterConfig
      );
    }
  }, [filterConfig]);

  function handleClose() {
    setOpen(false);
  }

  const { control, getValues, reset, setValue } =
    useForm<PaginatedFilterConfig>({
      defaultValues: getDefaults(),
    });

  useEffect(() => {
    void handleFiltering();
  }, [viewMode]);

  function countActiveFilters() {
    const count = Object.entries(getValues())
      .filter(
        ([k, _v]) =>
          ![
            'searchTerm',
            'orderBy',
            'orderDirection',
            'showOnlyMembers',
          ].includes(k)
      )
      .reduce((acc, [_key, val]) => {
        if (!(val as FilterOptionDto[])?.length) return acc;
        return ++acc;
      }, 0);
    return count;
  }

  function handleOpenFilters() {
    setOpen(true);
  }

  async function handleReset() {
    reset(getDefaults(true));
    window.localStorage.removeItem('filterConfig');
    await handleFiltering();
  }

  function toggleMeMembersPassThrough() {
    setValue('page-start', 0);
    void handleFiltering();
  }

  async function handleFiltering(resetPage?: boolean): Promise<void> {
    const filterValues = getValues();

    if (resetPage) {
      filterValues['page-start'] = 0;
    }

    window.localStorage.setItem(
      'filterConfig',
      JSON.stringify({ ...filterValues })
    );

    const filterConfig = {};
    if ((filterValues?.guideIds as FilterOptionDto[])?.length) {
      filterConfig['guideIds'] = (filterValues?.guideIds as FilterOptionDto[])
        ?.map(g => g.value)
        .join(',');
    }
    if (filterValues.showOnlyMyMembers) {
      // If a user is filtering by their own members,
      // They can't also filter by other guides
      if (filterConfig['guideIds']?.length) {
        setValue('guideIds', []);
      }
      filterConfig['guideIds'] = [actingUser?.id];
    }

    Object.entries(getValues())
      .filter(
        ([k, v]) =>
          ![
            'showOnlyMyMembers',
            'page-start',
            'page-size',
            'orderBy',
            'orderDirection',
            'searchTerm',
          ].includes(k) && (v as FilterOptionDto[])?.length
      )
      .forEach(([key, val]) => {
        filterConfig[key] = val as FilterOptionDto[];
      });

    if (viewMode === 'map-view') {
      filterConfig['forMap'] = true;
    } else {
      filterConfig['page-start'] = filterValues['page-start'];
      filterConfig['page-size'] = filterValues['page-size'];
      filterConfig['includeCount'] = true;
      filterConfig['orderBy'] = filterValues['orderBy'];
      filterConfig['orderDirection'] = filterValues['orderDirection'];
      filterConfig['searchTerm'] = filterValues['searchTerm'];
    }
    setFilterConfig({
      ...filterConfig,
      includeDetails: true,
      includeCohorts: true,
      includeStageAndStatus: true,
    });

    handleClose();
  }

  const dispatch = useAppDispatch();

  async function getPaginatedMembers(params: PaginatedFilterConfig) {
    await dispatch(await getPaginatedMembersForTable({ params }));
  }

  async function getMembersForMapView(params: MapFilterConfig) {
    await dispatch(await getMembersForMap({ params }));
  }

  function handleSortChange(orderBy: string, orderDirection: string) {
    const filterValues = getValues();
    const newValues = {
      ...filterValues,
      'page-start': 0,
      orderBy,
      orderDirection,
    };
    reset(newValues);
    void handleFiltering();
  }

  function handlePageChange(newPageNumber: number) {
    const filterValues = getValues();
    const newValues = { ...filterValues, 'page-start': newPageNumber };
    reset(newValues);
    void handleFiltering();
  }

  function handleRowsPerPageChange(newPageSize: number) {
    const filterValues = getValues();
    const newValues = {
      ...filterValues,
      'page-start': 0,
      'page-size': newPageSize,
    };
    reset(newValues);
    void handleFiltering();
  }

  return (
    <>
      <FlexContainer
        direction="column"
        alignItems="center"
        height={isMobile ? 'auto' : '100%'}
        width="100%"
        backgroundColor="#ffffff"
        borderRadius="lessRounded"
        sx={{
          flex: 1,
          maxWidth: '100%',
          overflow: isMobile ? 'visible' : 'hidden',
        }}
      >
        <MemberFilters
          control={control}
          handleOpenFilters={handleOpenFilters}
          handleFiltering={() => handleFiltering(true)}
          toggleMyMembers={toggleMeMembersPassThrough}
          viewMode={viewMode}
          setViewMode={setViewMode}
          activeFilterCount={countActiveFilters()}
          currentFilterConfig={filterConfig}
        />
        {viewMode === 'list-view' && (
          <FlexContainer
            direction="column"
            width="100%"
            sx={{ flex: 1, overflow: 'hidden' }}
          >
            <MembersTable
              handlePageChange={handlePageChange}
              handleRowsPerPageChange={handleRowsPerPageChange}
              handleSortChange={handleSortChange}
            />
          </FlexContainer>
        )}
        {viewMode === 'map-view' && (
          <Suspense fallback={<div>Loading map...</div>}>
            <MemberMap />
          </Suspense>
        )}
        <FilterDialog
          open={open}
          control={control}
          filters={filters}
          handleReset={handleReset}
          handleClose={handleClose}
          handleFiltering={() => handleFiltering(true)}
        />
      </FlexContainer>
      <AppHeaderPortal>
        <FlexContainer direction="row" sx={{ paddingLeft: 2 }}>
          <Typography variant="titleLarge" color="primary">
            Members
          </Typography>
        </FlexContainer>
      </AppHeaderPortal>
    </>
  );
};
